import React, { useEffect, useState } from "react";
import axios from "axios";
import SmoothLivePlayer from "../components/SmoothLivePlayer";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FaUsers,
  FaTruck,
  FaFolderOpen,
  FaPlay,
  FaStop,
  FaExpand,
  FaVideo,
  FaClock,
  FaLayerGroup,
  FaFilm,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

function StatusBadge({ running, text }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
        ${
          running
            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
            : "bg-slate-200/50 text-slate-500 border border-slate-200"
        }`}
    >
      <span className="relative flex h-2 w-2">
        {running && (
          <span className="animate-pulseRing absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${
            running ? "bg-emerald-400" : "bg-slate-400"
          }`}
        />
      </span>
      {running ? "Live" : text || "Offline"}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, color }) {
  const colors = {
    blue: {
      bg: "bg-blue-50",
      icon: "bg-blue-100 text-blue-600",
      val: "text-blue-700",
    },
    green: {
      bg: "bg-emerald-50",
      icon: "bg-emerald-100 text-emerald-600",
      val: "text-emerald-700",
    },
    purple: {
      bg: "bg-violet-50",
      icon: "bg-violet-100 text-violet-600",
      val: "text-violet-700",
    },
  };
  const c = colors[color] || colors.blue;

  return (
    <div className={`card card-hover p-5 flex items-center gap-4 ${c.bg} border-0`}>
      <div
        className={`flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-lg ${c.icon}`}
      >
        <Icon />
      </div>
      <div>
        <div className="text-xs font-medium text-slate-500 mb-0.5">{label}</div>
        <div className={`text-2xl font-bold ${c.val}`}>{value}</div>
      </div>
    </div>
  );
}

function MetaChip({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
      <Icon className="text-slate-400 text-sm flex-shrink-0" />
      <div>
        <div className="text-[10px] text-slate-400 uppercase tracking-wide font-medium">
          {label}
        </div>
        <div className="text-sm font-semibold text-slate-700 truncate">
          {String(value)}
        </div>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-card-md px-4 py-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.fill }} className="font-medium">
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState([]);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [liveRunning, setLiveRunning] = useState(false);
  const [liveStatusText, setLiveStatusText] = useState("idle");
  const [liveFrames, setLiveFrames] = useState(0);
  const [liveLastTs, setLiveLastTs] = useState("-");
  const [liveError, setLiveError] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");

  useEffect(() => {
    if (!localStorage.getItem("auth")) window.location.href = "/login";
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        const statsRes = await axios.get(`${API_BASE_URL}/api/detections/stats`);
        setStats(statsRes.data || []);

        const latestRes = await axios.get(`${API_BASE_URL}/api/detections/latest`);
        setLatest(latestRes.data || null);
      } catch (err) {
        console.error("Dashboard fetch error", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    let t;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/live/status`);
        setLiveRunning(data?.status === "running");
        setLiveStatusText(data?.status || "idle");
        setLiveFrames(data?.frames ?? 0);

        const ts = data?.last_result?.timestamp;
        setLiveLastTs(ts ? ts.replace("T", " ").replace("Z", "") : "-");
        setLiveError("");
      } catch {
        setLiveError("Backend unreachable");
      }

      t = setTimeout(poll, 3000);
    };

    poll();
    return () => clearTimeout(t);
  }, []);

  const startLive = async () => {
    try {
      setLiveError("");
      const payload = sourceUrl ? { url: sourceUrl } : {};
      const { data } = await axios.post(`${API_BASE_URL}/live/start`, payload);
      if (!data.ok) throw new Error(data.error || "Start failed");
    } catch (e) {
      setLiveError(e.message || String(e));
    }
  };

  const stopLive = async () => {
    try {
      setLiveError("");
      await axios.post(`${API_BASE_URL}/live/stop`);
    } catch (e) {
      setLiveError(e.message || String(e));
    }
  };

  const totalPersons = stats.reduce((s, r) => s + (r.person_total || 0), 0);
  const totalMachinery = stats.reduce((s, r) => s + (r.machinery_total || 0), 0);
  const totalFiles = stats.reduce((s, r) => s + (r.file_count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex items-center gap-3 text-slate-400">
          <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm font-medium">Loading dashboard…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-screen-xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Monitor your construction site in real time.
          </p>
        </div>
        <div className="text-xs text-slate-400 hidden sm:block">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total Persons" value={totalPersons} icon={FaUsers} color="blue" />
        <StatCard label="Total Machinery" value={totalMachinery} icon={FaTruck} color="green" />
        <StatCard label="Total Files" value={totalFiles} icon={FaFolderOpen} color="purple" />
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-slate-900 flex items-center justify-center text-white">
              <FaVideo className="text-xs" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">Live Stream</div>
              <div className="text-xs text-slate-400">AI detection feed</div>
            </div>
          </div>
          <StatusBadge running={liveRunning} text={liveStatusText} />
        </div>

        <div className="flex items-center gap-2 flex-wrap px-5 py-3 bg-slate-50 border-b border-slate-100">
          <button onClick={startLive} className="btn-primary">
            <FaPlay className="text-xs" /> Start
          </button>
          <button
            onClick={stopLive}
            disabled={!liveRunning}
            className="btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <FaStop className="text-xs" /> Stop
          </button>
          <input
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="Override source URL (optional)"
            className="flex-1 min-w-[220px] px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl
              placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
              transition-all duration-200"
          />
          {liveError && (
            <span className="text-xs text-red-500 font-medium px-3 py-2 bg-red-50 rounded-xl border border-red-100">
              {liveError}
            </span>
          )}
        </div>

        <div className="w-full bg-slate-950 flex items-center justify-center" style={{ minHeight: 400 }}>
          {liveRunning ? (
            <SmoothLivePlayer />
          ) : (
            <div className="flex flex-col items-center gap-4 py-16 px-6 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center">
                <FaFilm className="text-2xl text-slate-500" />
              </div>
              <div>
                <div className="text-base font-semibold text-slate-300">
                  Stream is offline
                </div>
                <div className="text-sm text-slate-500 mt-1">
                  Press Start to begin live detection feed.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 bg-slate-50 border-t border-slate-100">
          <MetaChip icon={FaLayerGroup} label="Frames" value={liveFrames} />
          <MetaChip icon={FaClock} label="Last Update" value={liveLastTs} />
          <MetaChip
            icon={FaVideo}
            label="Status"
            value={liveRunning ? "Running" : liveStatusText}
          />
          <div />
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center text-white">
              <FaLayerGroup className="text-xs" />
            </div>
            <div>
              <div className="font-semibold text-slate-900 text-sm">3D Render Preview</div>
              <div className="text-xs text-slate-400">Unity WebGL — final site model</div>
            </div>
          </div>
          <button
            onClick={() => {
              const iframe = document.getElementById("unity-iframe");
              if (iframe?.requestFullscreen) iframe.requestFullscreen();
            }}
            className="btn-secondary text-xs gap-1.5"
          >
            <FaExpand className="text-xs" /> Fullscreen
          </button>
        </div>
        <div className="bg-slate-100">
          <iframe
            id="unity-iframe"
            src="/unity/unityugl.html"
            title="Unity Render"
            className="w-full block"
            style={{ border: "none", height: 520 }}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div className="section-title">Latest Detection</div>
            {latest && (
              <span className="badge bg-slate-100 text-slate-600 border border-slate-200">
                {latest.date}
              </span>
            )}
          </div>

          {latest ? (
            <div className="space-y-4">
              <img
                src={`${API_BASE_URL}/images/${latest.date}/${
                  latest.image ||
                  (latest.result_file
                    ? latest.result_file.replace("_result.txt", ".jpg")
                    : "")
                }`}
                alt="Latest detection"
                className="w-full h-44 object-cover rounded-xl border border-slate-100"
              />
              <div className="text-sm text-slate-500 truncate">
                {latest.file || latest.result_file || latest.image}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
                  <div className="text-xs text-blue-500 font-medium mb-0.5">Persons</div>
                  <div className="text-2xl font-bold text-blue-700">{latest.person_count}</div>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                  <div className="text-xs text-emerald-500 font-medium mb-0.5">Machinery</div>
                  <div className="text-2xl font-bold text-emerald-700">{latest.machinery_count}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <FaFolderOpen className="text-slate-400 text-lg" />
              </div>
              <div className="text-sm text-slate-500">No detection data yet.</div>
            </div>
          )}
        </div>

        <div className="card p-5 lg:col-span-3">
          <div className="section-title mb-4">Detection Trends</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.slice(-7)} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f8fafc" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#64748b", paddingTop: 12 }} iconType="circle" iconSize={8} />
              <Bar dataKey="person_total" fill="#3b82f6" name="Persons" radius={[4, 4, 0, 0]} />
              <Bar dataKey="machinery_total" fill="#10b981" name="Machinery" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;